import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, MessageCircle, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { staggerContainer, staggerItem, slideLeft, slideRight } from '../hooks/useAnimations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error('Completa los campos requeridos'); return; }
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) { toast.success('Mensaje enviado. Nuestro concierge te contactara pronto.'); setForm({ name: '', email: '', phone: '', message: '' }); }
      else { toast.error('Error al enviar'); }
    } catch { toast.error('Error de conexion'); }
    finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-20" data-testid="contact-page">
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            {/* Form - Slide from left */}
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={staggerContainer}>
              <motion.p variants={staggerItem} className="overline mb-3">Concierge Privado</motion.p>
              <motion.h1 className="text-4xl md:text-5xl font-bold tracking-tighter text-white overflow-hidden" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                <motion.span className="inline-block" initial={{ y: 70 }} whileInView={{ y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}>
                  Tu Proxima Ronda
                </motion.span>
                <br />
                <motion.span className="inline-block" initial={{ y: 70 }} whileInView={{ y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1, duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}>
                  Legendaria Comienza Aqui.
                </motion.span>
              </motion.h1>
              <motion.p variants={staggerItem} className="text-base text-zinc-400 mt-4 mb-10">
                Cuentanos tu vision. Nuestro equipo de especialistas disenara una experiencia completamente personalizada.
              </motion.p>
              <motion.form variants={staggerItem} onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <input type="text" placeholder="Nombre *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="bg-white/5 border border-white/10 px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30 transition-colors duration-300" data-testid="contact-name" />
                  <input type="email" placeholder="Email *" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="bg-white/5 border border-white/10 px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30 transition-colors duration-300" data-testid="contact-email" />
                </div>
                <input type="tel" placeholder="Telefono" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30 transition-colors duration-300" data-testid="contact-phone" />
                <textarea placeholder="Describenos tu experiencia de golf ideal... *" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30 min-h-[160px] resize-none transition-colors duration-300" data-testid="contact-message" />
                <button type="submit" disabled={sending}
                  className="w-full bg-white text-black py-4 text-sm font-medium hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="contact-submit">
                  {sending ? 'Enviando...' : <><Send size={16} /> Solicitar Experiencia Personalizada</>}
                </button>
              </motion.form>
            </motion.div>

            {/* Info - Slide from right with stagger */}
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }}
              variants={staggerContainer} className="space-y-10 pt-0 md:pt-20">
              <motion.div variants={slideRight}>
                <p className="overline mb-6">Contacto Directo</p>
                <div className="space-y-6">
                  {[
                    { icon: MessageCircle, label: 'WhatsApp Concierge', value: '+52 444 204 9874', href: 'https://wa.me/524442049874?text=Hola,%20me%20interesa%20una%20experiencia%20de%20golf%20personalizada' },
                    { icon: Mail, label: 'Email', value: 'info@elitegolfmx.mx', href: 'mailto:info@elitegolfmx.mx' },
                    { icon: Phone, label: 'Telefono', value: '+52 444 204 9874', href: 'tel:+524442049874' },
                  ].map((item) => (
                    <motion.div key={item.label} initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                      transition={{ delay: 0.3, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                      className="flex items-start gap-4">
                      <div className="p-3 border border-white/10 transition-colors duration-300 hover:border-white/25">
                        <item.icon size={18} className="text-zinc-500" />
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider">{item.label}</p>
                        <a href={item.href} target="_blank" rel="noreferrer" className="text-sm text-white hover:text-zinc-300 transition-colors" data-testid={`contact-info-${item.label.toLowerCase().replace(/ /g, '-')}`}>
                          {item.value}
                        </a>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              <motion.div variants={slideRight}
                className="p-6 bg-[#0F291E] border border-white/5 transition-all duration-500 hover:border-white/10">
                <p className="text-sm font-medium text-white mb-2">Respuesta Inmediata</p>
                <p className="text-sm text-zinc-400 mb-4">Para atencion inmediata y personalizada, contacta directamente a nuestro concierge por WhatsApp.</p>
                <a href="https://wa.me/524442049874?text=Hola,%20me%20interesa%20una%20experiencia%20de%20golf%20personalizada"
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 text-sm font-medium hover:bg-[#20bd5a] transition-colors"
                  data-testid="whatsapp-cta">
                  <MessageCircle size={16} /> Hablar con Concierge
                </a>
              </motion.div>

              <motion.div variants={slideRight} className="p-6 border border-white/5">
                <p className="text-xs text-zinc-600 leading-relaxed">
                  Cada solicitud es atendida personalmente por nuestro equipo de especialistas.
                  Respuesta garantizada en menos de 24 horas. Discrecion y privacidad absoluta.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
